import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button, Container, Form, Spinner } from "react-bootstrap";
import { Helmet } from "react-helmet";
import {
  BsBroadcast,
  BsFilter,
  BsFire,
  BsGridFill,
  BsListUl,
  BsPencil,
  BsSearch,
  BsStarFill,
  BsTagFill,
  BsTrash,
  BsX,
} from "react-icons/bs";
import useAuth from "../../contexts/auth";
import {
  getOfertasAoVivo,
  getCupons,
  deleteCupom,
  deleteOfertaAoVivo,
  updateOfertaAoVivo,
  addOfertaAoVivoToLive,
  clearOfertasAoVivo,
  setOfertaDestaque,
} from "../../services/api";
import OfertaCard from "./OfertaCard";
import OfertaQuickForm from "./OfertaQuickForm";
import CupomModal from "./CupomModal";
import { CATEGORIAS, MARCAS, normalize as norm, offerCategory, offerBrand, offerMatchesSelos, liveCopyMatches } from "./offerPresentation";
import "./styles.scss";

const LIVE_INTERVAL_MS = 5000;
const PRICE_STEP = 50;
const HIDE_QUENTES = process.env.REACT_APP_HIDE_QUENTES === "true";

export default function OfertasAoVivo() {
  const { user } = useAuth();
  const isAdmin = user && user.type === "admin";
  const isPoster = user && user.type === "poster";
  const canManage = isAdmin || isPoster;

  const [tab, setTab] = useState("live");
  const [view, setView] = useState("list");
  const [isMobile, setIsMobile] = useState(false);
  const [mobileSearchSlot, setMobileSearchSlot] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [copiedCupom, setCopiedCupom] = useState(null);
  const [query, setQuery] = useState("");
  const [cats, setCats] = useState(() => Object.fromEntries(CATEGORIAS.map((item) => [item, true])));
  const [marcas, setMarcas] = useState(() => Object.fromEntries(MARCAS.map((item) => [item, true])));
  const [selos, setSelos] = useState({
    Excelente: true,
    Bom: true,
    Esgotou: true,
  });
  const [pMin, setPMin] = useState(0);
  const [pMax, setPMax] = useState(6000);
  const [pMaxTouched, setPMaxTouched] = useState(false);

  const [offers, setOffers] = useState([]);
  const [cupons, setCupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastSync, setLastSync] = useState(null);
  const [addedToLive, setAddedToLive] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("oavAddedToLive") || "[]");
      return Array.isArray(saved) ? saved : [];
    } catch (e) {
      return [];
    }
  });

  const [liveCopies, setLiveCopies] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("oavAddedToLiveCopies") || "{}");
      return saved && typeof saved === "object" && !Array.isArray(saved) ? saved : {};
    } catch (_) {
      return {};
    }
  });
  const addingToLiveRef = useRef(new Set());

  useEffect(() => {
    try {
      localStorage.setItem("oavAddedToLive", JSON.stringify(addedToLive));
      localStorage.setItem("oavAddedToLiveCopies", JSON.stringify(liveCopies));
    } catch (_) {
      /* mantém o estado em memória se o armazenamento estiver indisponível */
    }
  }, [addedToLive, liveCopies]);

  const currentAddedToLive = useMemo(() => {
    const live = offers.filter(o => (o.board || "ao_vivo") === "ao_vivo");
    const liveIds = new Set(live.map(o => String(o.id)));
    return addedToLive.filter(id => {
      if (liveCopies[id]) {
        // Uma resposta no limite pode não conter todas as cópias existentes.
        return liveIds.has(String(liveCopies[id])) || offers.length >= 200;
      }
      // Compatibilidade com marcações antigas, que só guardavam o ID de Quentes.
      const source = offers.find(o => String(o.id) === String(id) && o.board === "quente");
      return !source || live.some(copy => liveCopyMatches(source, copy));
    });
  }, [offers, addedToLive, liveCopies]);

  const [editing, setEditing] = useState(null);
  const editorRef = useRef(null);
  const [showCupom, setShowCupom] = useState(false);
  const [editingCupom, setEditingCupom] = useState(null);

  const filtersRef = useRef(null);
  const filterBtnRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const update = (event) => setIsMobile(event.matches);
    setIsMobile(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);

  const effectiveView = isMobile ? "list" : view;

  useEffect(() => {
    if (!isMobile) {
      setMobileSearchSlot(null);
      return undefined;
    }
    // The existing navbar owns navigation; the page owns this single search/filter control.
    const syncSlot = () => setMobileSearchSlot(document.getElementById("oav-mobile-search-slot"));
    syncSlot();
    const observer = new MutationObserver(syncSlot);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [isMobile]);

  const closeFilters = useCallback(() => {
    setShowFilters(false);
    window.setTimeout(() => filterBtnRef.current?.focus(), 0);
  }, []);

  useEffect(() => {
    if (!showFilters) return undefined;
    const panel = filtersRef.current;
    panel?.focus();
    const onKey = (event) => {
      if (event.key === "Escape") closeFilters();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showFilters, closeFilters]);

  const priceCeil = useMemo(() => {
    const max = offers.reduce((m, o) => Math.max(m, Number(o.offer_price) || 0), 0);
    return Math.max(6000, Math.ceil(max / PRICE_STEP) * PRICE_STEP);
  }, [offers]);

  const destaques = useMemo(
    () => offers.filter((o) => o.destaque && (o.board || "ao_vivo") === "ao_vivo"),
    [offers]
  );

  const destaqueTrackRef = useRef(null);
  const destaquePausedRef = useRef(false);
  useEffect(() => {
    const el = destaqueTrackRef.current;
    if (!el || isMobile || destaques.length < 2) return undefined;
    let raf;
    let pos = el.scrollLeft;
    const step = () => {
      if (!destaquePausedRef.current && el.scrollWidth > el.clientWidth + 4) {
        const half = el.scrollWidth / 2;
        pos += 0.3;
        if (pos >= half) pos -= half;
        el.scrollLeft = pos;
      } else {
        pos = el.scrollLeft;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [destaques.length, isMobile]);

  const destaqueIds = destaques.map(offer => offer.id).join(",");
  useEffect(() => {
    if (isMobile && destaqueTrackRef.current) destaqueTrackRef.current.scrollLeft = 0;
  }, [isMobile, destaqueIds]);

  const scrollDestaque = (dir) => {
    const el = destaqueTrackRef.current;
    if (!el) return;
    if (isMobile) {
      const step = (el.firstElementChild?.getBoundingClientRect().width || el.clientWidth) + 12;
      const index = Math.max(0, Math.min(destaques.length - 1, Math.round(el.scrollLeft / step) + dir));
      el.scrollTo({ left: index * step, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
      return;
    }
    destaquePausedRef.current = true;
    el.scrollBy({ left: Math.round(el.clientWidth * 0.8) * dir, behavior: "smooth" });
    window.clearTimeout(el._oavResume);
    el._oavResume = window.setTimeout(() => {
      destaquePausedRef.current = false;
    }, 2500);
  };

  useEffect(() => {
    if (!pMaxTouched) setPMax(priceCeil);
  }, [priceCeil, pMaxTouched]);

  const load = useCallback(async (opts = {}) => {
    const { silent = false } = opts;
    if (!silent) setLoading(true);
    try {
      const [o, c] = await Promise.all([getOfertasAoVivo({ limit: 200 }), getCupons()]);
      const list = Array.isArray(o?.data) ? o.data : [];
      setOffers(list);
      setCupons(Array.isArray(c?.data) ? c.data : []);
      setLastSync(new Date());
    } catch (e) {
      /* mantém estado atual */
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === "visible") load({ silent: true });
    };
    const t = setInterval(refresh, LIVE_INTERVAL_MS);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [load]);

  const setQ = async (id, selo) => {
    setOffers((prev) => prev.map((o) => (o.id === id ? { ...o, selo } : o)));
    try {
      await updateOfertaAoVivo(id, { selo });
    } catch (e) {
      window.alert("Não foi possível salvar o selo. Atualize a página para conferir o estado salvo.");
    } finally {
      load({ silent: true });
    }
  };

  const setLive = async (o) => {
    if (addingToLiveRef.current.has(o.id)) return;
    addingToLiveRef.current.add(o.id);
    try {
      const result = await addOfertaAoVivoToLive(o.id);
      if (result?.success === false) throw new Error(result.error || "Falha ao adicionar à Live");
      const copy = result?.data;
      if (copy?.id) {
        setLiveCopies(prev => ({ ...prev, [o.id]: copy.id }));
        setOffers(prev => prev.some(item => item.id === copy.id) ? prev : [copy, ...prev]);
      }
      setAddedToLive((prev) => {
        const next = prev.includes(o.id) ? prev : [...prev, o.id];
        return next;
      });
      load({ silent: true });
    } catch (e) {
      window.alert("Não foi possível adicionar às Ofertas Live. Atualize a página para conferir.");
    } finally {
      addingToLiveRef.current.delete(o.id);
    }
  };

  const setDestaque = async (o) => {
    const next = !o.destaque;
    setOffers((prev) => prev.map((x) => (x.id === o.id ? { ...x, destaque: next } : x)));
    try {
      await setOfertaDestaque(o.id, next);
    } catch (e) {
      window.alert("Não foi possível atualizar o destaque.");
      load({ silent: true });
    }
  };

  const openEdit = (o) => {
    if (canManage && editorRef.current?.edit(o)) {
      setTab((o.board || "ao_vivo") === "quente" ? "quentes" : "live");
    }
  };

  const toggleCat = (id) => setCats((prev) => ({ ...prev, [id]: !prev[id] }));
  const toggleMarca = (m) => setMarcas((prev) => ({ ...prev, [m]: !prev[m] }));
  const toggleSelo = (s) => setSelos((prev) => ({ ...prev, [s]: !prev[s] }));

  const visible = useMemo(() => {
    const q = norm(query.trim());
    const catSelected = Object.keys(cats).filter((k) => cats[k]);
    const marcaSelected = Object.keys(marcas).filter((k) => marcas[k]);

    return offers
      .filter((o) =>
        tab === "live"
          ? (o.board || "ao_vivo") === "ao_vivo"
          : o.board === "quente"
      )
      .filter((o) => {
        if (!catSelected.includes(offerCategory(o))) return false;
        if (!marcaSelected.includes(offerBrand(o))) return false;
        if (!offerMatchesSelos(o, selos)) return false;
        const price = Number(o.offer_price) || 0;
        if (price > 0 && (price < pMin || price > pMax)) return false;
        if (q && !norm(o.name).includes(q) && !norm(o.cupom).includes(q) && !norm(o.code || "").includes(q)) return false;
        return true;
      })
      .slice()
      .sort((a, b) => {
        if (canManage) return 0;
        const ea = (a.selo || "Neutro") === "Esgotou" ? 1 : 0;
        const eb = (b.selo || "Neutro") === "Esgotou" ? 1 : 0;
        return ea - eb;
      });
  }, [offers, tab, query, cats, marcas, selos, pMin, pMax, canManage]);

  const copyCupom = (codigo) => {
    if (navigator.clipboard) navigator.clipboard.writeText(codigo);
    setCopiedCupom(codigo);
    setTimeout(() => setCopiedCupom((v) => (v === codigo ? null : v)), 1500);
  };

  const removeCupom = async (c) => {
    if (!window.confirm(`Remover o cupom ${c.codigo}?`)) return;
    await deleteCupom(c.id);
    load({ silent: true });
  };

  const removeOferta = async (o) => {
    if (!window.confirm(`Excluir a oferta "${o.name}"?`)) return;
    await deleteOfertaAoVivo(o.id);
    if ((o.board || "ao_vivo") === "ao_vivo") {
      const released = addedToLive.filter(id => liveCopies[id]
        ? String(liveCopies[id]) === String(o.id)
        : offers.some(source => String(source.id) === String(id) && source.board === "quente" && liveCopyMatches(source, o)));
      setAddedToLive(prev => prev.filter(id => !released.includes(id)));
      setLiveCopies(prev => Object.fromEntries(Object.entries(prev).filter(([id]) => !released.some(sourceId => String(sourceId) === id))));
    }
    setOffers((prev) => prev.filter((x) => x.id !== o.id));
  };

  const clearBoard = async () => {
    const isLive = tab === "live";
    const label = isLive ? "Ofertas Live" : "Ofertas Quentes";
    if (
      !window.confirm(
        `Apagar TODAS as ofertas de ${label}? Essa ação não pode ser desfeita.`
      )
    ) {
      return;
    }
    try {
      await clearOfertasAoVivo(isLive ? "ao_vivo" : "quente");
      if (isLive) {
        setAddedToLive([]);
        setLiveCopies({});
      }
      setOffers((prev) =>
        prev.filter((o) => (isLive ? o.board !== "ao_vivo" : o.board !== "quente"))
      );
    } catch (e) {
      window.alert("Não foi possível apagar as ofertas.");
    }
  };

  const quickForm = canManage ? (
    <OfertaQuickForm ref={editorRef} variant={effectiveView} visible={tab === "live" || !!editing}
      onEditingChange={setEditing} onSaved={(result) => {
        const item = result?.data;
        if (item?.id) setOffers(prev => prev.some(o => o.id === item.id) ? prev.map(o => o.id === item.id ? { ...o, ...item } : o) : [item, ...prev]);
        load({ silent: true });
      }} />
  ) : null;

  const searchControls = (
    <div className="oav-toolbar-left">
      <div className="oav-search">
        <BsSearch aria-hidden="true" />
        <Form.Control type="search" placeholder="Pesquisar ofertas..." value={query}
          onChange={(e) => setQuery(e.target.value)} aria-label="Pesquisar ofertas" />
      </div>
      <button type="button" ref={filterBtnRef}
        className={`oav-filters-toggle ${showFilters ? "is-active" : ""}`}
        onClick={() => setShowFilters((v) => !v)} aria-expanded={showFilters}
        aria-controls="oav-filters" aria-label="Filtros de ofertas">
        <BsFilter /><span>Filtros</span>
      </button>
    </div>
  );

  return (
    <div className="ofertas-ao-vivo">
      <Helmet title="Ofertas Ao Vivo – Caçadores de Ofertas" />
      {isMobile && mobileSearchSlot ? createPortal(searchControls, mobileSearchSlot) : null}

      <Container className="oav-main">
        <section className="oav-cupons-box">
          <h3 className="oav-cupons-title">
            <BsTagFill /> Cupons Ativos
          </h3>
          <div className="oav-cupons-row">
            {canManage && (
              <Button
                variant="primary"
                size="sm"
                className="oav-add-cupom"
                onClick={() => {
                  setEditingCupom(null);
                  setShowCupom(true);
                }}
              >
                Adicionar Cupom +
              </Button>
            )}
            <div className="oav-cupons">
              {cupons.map((c) => (
                <span
                  key={c.id}
                  className={`oav-cupom ${c.link ? "com-link" : ""} ${
                    c.cores && c.cores[0] ? `is-${c.cores[0]}` : ""
                  }`}
                >
                  {c.link ? (
                    <a
                      href={c.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => copyCupom(c.codigo)}
                      title="Copia o cupom e abre o site"
                    >
                      {copiedCupom === c.codigo ? "Copiado!" : c.codigo}
                    </a>
                  ) : (
                    <button
                      type="button"
                      className="oav-cupom-copy"
                      onClick={() => copyCupom(c.codigo)}
                      title="Clique para copiar o cupom"
                    >
                      {copiedCupom === c.codigo ? "Copiado!" : c.codigo}
                    </button>
                  )}
                  {canManage && (
                    <button
                      type="button"
                      className="oav-cupom-edit"
                      onClick={() => {
                        setEditingCupom(c);
                        setShowCupom(true);
                      }}
                      title="Editar cupom"
                      aria-label="Editar cupom"
                    >
                      <BsPencil aria-hidden="true" />
                    </button>
                  )}
                  {canManage && (
                    <button
                      type="button"
                      className="x"
                      onClick={() => removeCupom(c)}
                      title="Remover"
                      aria-label="Remover cupom"
                    >
                      <BsX aria-hidden="true" />
                    </button>
                  )}
                </span>
              ))}
              {cupons.length === 0 && (
                <span className="text-muted small">Nenhum cupom</span>
              )}
            </div>
          </div>
        </section>

        {destaques.length > 0 && (
          <section className="oav-destaque">
            <h3 className="oav-destaque-title">
              <BsStarFill /> Ofertas Destaque
            </h3>
            <div className="oav-destaque-wrap">
              {(!isMobile || destaques.length > 1) && <button
                type="button"
                className="oav-destaque-arrow left"
                onClick={() => scrollDestaque(-1)}
                aria-label="Anterior"
              >
                ‹
              </button>}
              <div
                className={`oav-destaque-track ${
                  !isMobile && destaques.length >= 2 ? "is-marquee" : ""
                }`}
                ref={destaqueTrackRef}
                onMouseEnter={() => {
                  destaquePausedRef.current = true;
                }}
                onMouseLeave={() => {
                  destaquePausedRef.current = false;
                }}
              >
                {(!isMobile && destaques.length >= 2 ? [...destaques, ...destaques] : destaques).map(
                  (o, i) => (
                    <div className="oav-destaque-item" key={`${o.id}-${i}`}>
                      <OfertaCard
                        product={o}
                        onQuality={setQ}
                        onDelete={removeOferta}
                        onEdit={openEdit}
                        onLive={setLive}
                        onDestaque={setDestaque}
                        showLive={false}
                        addedToLive={currentAddedToLive}
                        isAdmin={canManage}
                        showCode={!!user}
                        variant="grid"
                      />
                    </div>
                  )
                )}
              </div>
              {(!isMobile || destaques.length > 1) && <button
                type="button"
                className="oav-destaque-arrow right"
                onClick={() => scrollDestaque(1)}
                aria-label="Próximo"
              >
                ›
              </button>}
            </div>
          </section>
        )}

        <div className="oav-toolbar">
          {!isMobile || !mobileSearchSlot ? searchControls : null}

          <div className="oav-toolbar-right">
            <div className="oav-view-toggle" role="group" aria-label="Modo de visualização">
              <button
                type="button"
                className={effectiveView === "list" ? "is-active" : ""}
                onClick={() => setView("list")}
                aria-label="Visualizar em lista"
                aria-pressed={effectiveView === "list"}
              >
                <BsListUl />
              </button>
              <button
                type="button"
                className={effectiveView === "grid" ? "is-active" : ""}
                onClick={() => setView("grid")}
                aria-label="Visualizar em grade"
                aria-pressed={effectiveView === "grid"}
              >
                <BsGridFill />
              </button>
            </div>

            <div className="oav-tabs" role="tablist">
              <button
                type="button"
                className={`oav-tab ${tab === "live" ? "is-active" : ""}`}
                onClick={() => setTab("live")}
                role="tab"
                aria-selected={tab === "live"}
              >
                <BsBroadcast /> Ofertas Live
              </button>
              {!HIDE_QUENTES && (
                <button
                  type="button"
                  className={`oav-tab ${tab === "quentes" ? "is-active" : ""}`}
                  onClick={() => setTab("quentes")}
                  role="tab"
                  aria-selected={tab === "quentes"}
                >
                  <BsFire /> Ofertas Quentes
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="oav-toolbar-meta">
          <div className="oav-live-indicator">
            <span className="dot" />
            {lastSync ? `Ao vivo · ${lastSync.toLocaleTimeString("pt-BR")}` : "Conectando..."}
          </div>
          {canManage && (
            <button
              type="button"
              className="oav-clear-btn"
              onClick={clearBoard}
              title={`Apagar todas as ofertas de ${
                tab === "live" ? "Ofertas Live" : "Ofertas Quentes"
              }`}
            >
              <BsTrash aria-hidden="true" /> Limpar {tab === "live" ? "Live" : "Quentes"}
            </button>
          )}
        </div>

        <div className="oav-results">
          {showFilters && (
            <div
              className="oav-filters-backdrop"
              onClick={closeFilters}
              aria-hidden="true"
            />
          )}

          <aside
            id="oav-filters"
            ref={filtersRef}
            className={`oav-aside ${showFilters ? "is-open" : ""}`}
            role="dialog"
            aria-modal="true"
            aria-label="Filtros de ofertas"
            aria-hidden={!showFilters}
            tabIndex={-1}
          >
            <div className="oav-aside-head">
              <h3>Filtros</h3>
              <button
                type="button"
                className="oav-aside-close"
                onClick={closeFilters}
                aria-label="Fechar filtros"
              >
                <BsX />
              </button>
            </div>

            <h4>Categorias</h4>
            <div className="oav-checks">
              {CATEGORIAS.map((c) => (
                <label key={c} className="oav-chk">
                  <input
                    type="checkbox"
                    checked={!!cats[c]}
                    onChange={() => toggleCat(c)}
                  />
                  {c}
                </label>
              ))}
            </div>

            <h4>Marcas</h4>
            <div className="oav-checks">
              {MARCAS.map((m) => (
                <label key={m} className="oav-chk">
                  <input
                    type="checkbox"
                    checked={!!marcas[m]}
                    onChange={() => toggleMarca(m)}
                  />
                  {m}
                </label>
              ))}
            </div>

            <h4>Selos</h4>
            <div className="oav-checks">
              {[
                { key: "Excelente", label: "Linha Azul (Excelente)", cls: "is-excelente" },
                { key: "Bom", label: "Linha Verde (Bom)", cls: "is-bom" },
                { key: "Esgotou", label: "Esgotado", cls: "is-esgotou" },
              ].map((s) => (
                <label key={s.key} className="oav-chk">
                  <input
                    type="checkbox"
                    checked={!!selos[s.key]}
                    onChange={() => toggleSelo(s.key)}
                  />
                  <span className={`oav-selo-key ${s.cls}`} />
                  {s.label}
                </label>
              ))}
            </div>

            <h4>Preço</h4>
            <div className="oav-preco-head">
              <b>
                R$ {pMin} - R$ {pMax}
              </b>
            </div>
            <div className="oav-range">
              <input
                type="range"
                min="0"
                max={priceCeil}
                step={PRICE_STEP}
                value={pMin}
                onChange={(e) => setPMin(Math.min(+e.target.value, pMax - PRICE_STEP))}
                aria-label="Preço mínimo"
              />
              <input
                type="range"
                min="0"
                max={priceCeil}
                step={PRICE_STEP}
                value={pMax}
                onChange={(e) => {
                  setPMaxTouched(true);
                  setPMax(Math.max(+e.target.value, pMin + PRICE_STEP));
                }}
                aria-label="Preço máximo"
              />
            </div>
          </aside>

          <div className={effectiveView === "grid" ? "oav-grid" : "oav-list"}>
            {quickForm}
            {loading ? (
            <div className="oav-results-message text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : visible.length === 0 ? (
            <div className="oav-empty oav-results-message">
              <b>Nenhuma oferta encontrada</b>
              <p className="text-muted">
                Ajuste os filtros ou mude de aba. Ofertas novas aparecem
                automaticamente.
              </p>
            </div>
          ) : visible.map((o) => (
                <OfertaCard
                  key={o.id}
                  product={o}
                  onQuality={setQ}
                  onDelete={removeOferta}
                  onEdit={openEdit}
                  onLive={setLive}
                  onDestaque={setDestaque}
                  showLive={tab === "quentes"}
                  addedToLive={currentAddedToLive}
                  isAdmin={canManage}
                  showCode={!!user}
                   variant={effectiveView}
                />
              ))}
          </div>
        </div>
      </Container>

      <CupomModal
        show={showCupom}
        cupom={editingCupom}
        onHide={() => {
          setShowCupom(false);
          setEditingCupom(null);
        }}
        onSaved={() => {
          setShowCupom(false);
          setEditingCupom(null);
          load({ silent: true });
        }}
      />
    </div>
  );
}
